/**
 * m3 echo Frontend - Chatbot Component
 *
 * A floating chatbot widget that provides m3 echo-related assistance
 * using Gemini Flash 2.5.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Divider,
  Fade,
  Tooltip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import apiClient from '../services/api';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

const Chatbot: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      text: 'Hallo! Ich bin Dein KI-basierter m3 echo-Assistent. Wie kann ich Dir helfen?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevMessageCountRef = useRef(1);

  /**
   * Scroll to bottom of messages
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Focus input when chat opens
   */
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [open]);

  /**
   * Toggle chat window
   */
  const toggleChat = () => {
    setOpen(!open);
  };

  /**
   * Send message to chatbot API
   */
  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    // Get current messages before adding new one (for history)
    const currentMessages = messages;
    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue('');

    // Build conversation history (exclude the initial greeting, include up to last 10 exchanges)
    const historyMessages = currentMessages
      .slice(1) // Skip initial bot greeting
      .slice(-20) // Keep last 20 messages (10 exchanges)
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text
      }));

    // Start API call immediately but delay showing loading indicator
    const apiCallPromise = apiClient.post('/chatbot/chat', {
      message: messageText,
      history: historyMessages,
    });

    // Wait 1 second before showing typing dots
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(true);

    try {
      const response = await apiCallPromise;

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.data.response,
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Chatbot error:', error);

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Entschuldigung, es gab einen Fehler bei der Verarbeitung Deiner Anfrage. Bitte versuche es erneut.',
        sender: 'bot',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  /**
   * Format timestamp
   */
  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Chat Window */}
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 72, sm: 90 },
            right: { xs: 16, sm: 24 },
            width: { xs: 'calc(100vw - 32px)', sm: 450 },
            height: { xs: 400, sm: 500 },
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #678BA3, #5A7A92)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon />
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                m3 echo Assistent
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={toggleChat}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              backgroundColor: '#F5F6F8',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((message, msgIndex) => {
              const isNewest = msgIndex === messages.length - 1 && msgIndex >= prevMessageCountRef.current;
              if (msgIndex === messages.length - 1) {
                prevMessageCountRef.current = messages.length;
              }
              return (
              <Fade key={message.id} in timeout={isNewest && !reduceMotion ? 300 : 0}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                {/* Avatar */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: message.sender === 'user' ? '#678BA3' : '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {message.sender === 'user' ? (
                    <PersonIcon sx={{ fontSize: 20, color: 'white' }} />
                  ) : (
                    <SmartToyIcon sx={{ fontSize: 20, color: '#678BA3' }} />
                  )}
                </Box>

                {/* Message Bubble */}
                <Box
                  sx={{
                    maxWidth: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      backgroundColor: message.sender === 'user' ? '#678BA3' : 'white',
                      color: message.sender === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      borderTopLeftRadius: message.sender === 'bot' ? 0 : 2,
                      borderTopRightRadius: message.sender === 'user' ? 0 : 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {message.text}
                    </Typography>
                  </Paper>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                      px: 0.5,
                    }}
                  >
                    {formatTime(message.timestamp)}
                  </Typography>
                </Box>
              </Box>
              </Fade>
              );
            })}

            {/* Loading Indicator - Typing Dots */}
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: '#E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 20, color: '#678BA3' }} />
                </Box>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    borderTopLeftRadius: 0,
                    display: 'flex',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#678BA3',
                      animation: 'typing 1.4s infinite',
                      '@keyframes typing': {
                        '0%, 60%, 100%': {
                          opacity: 0.3,
                          transform: 'translateY(0)',
                        },
                        '30%': {
                          opacity: 1,
                          transform: 'translateY(-8px)',
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#678BA3',
                      animation: 'typing 1.4s infinite 0.2s',
                      '@keyframes typing': {
                        '0%, 60%, 100%': {
                          opacity: 0.3,
                          transform: 'translateY(0)',
                        },
                        '30%': {
                          opacity: 1,
                          transform: 'translateY(-8px)',
                        },
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#678BA3',
                      animation: 'typing 1.4s infinite 0.4s',
                      '@keyframes typing': {
                        '0%, 60%, 100%': {
                          opacity: 0.3,
                          transform: 'translateY(0)',
                        },
                        '30%': {
                          opacity: 1,
                          transform: 'translateY(-8px)',
                        },
                      },
                    }}
                  />
                </Paper>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Divider />

          {/* Input */}
          <Box sx={{ p: 2, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                placeholder="Stelle Deine Frage zu m3 echo..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                multiline
                maxRows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                sx={{
                  backgroundColor: '#678BA3',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#4D6B80',
                  },
                  '&:disabled': {
                    backgroundColor: '#E5E7EB',
                    color: '#9CA3AF',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1,
                textAlign: 'center',
              }}
            >
              Ich beantworte nur Fragen zu m3 echo
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Floating Action Button */}
      <Tooltip title="m3 echo Assistent" placement="right">
        <Fab
          color="primary"
          onClick={toggleChat}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            backgroundColor: '#678BA3',
            '&:hover': {
              backgroundColor: '#4D6B80',
            },
            zIndex: 1300,
            ...(!open && !reduceMotion && {
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                border: '2px solid #678BA3',
                animation: 'fabRingPulse 2s ease-out infinite',
                '@keyframes fabRingPulse': {
                  '0%': { transform: 'scale(1)', opacity: 0.6 },
                  '100%': { transform: 'scale(1.4)', opacity: 0 },
                },
              },
            }),
          }}
        >
          {open ? <CloseIcon /> : <ChatIcon />}
        </Fab>
      </Tooltip>
    </>
  );
};

export default Chatbot;
